import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Box, Weight, Boxes, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { 
  EXPORT_WEEKLY_MAX_WEIGHT_KG, 
  EXPORT_WEEKLY_MAX_VOLUME_M3,
  getLimitColorStatus,
  formatLimitDisplay 
} from '../../utils/orderLimits';

const ProductsSummaryCard = ({ products = [], productos = [], showLimits = false, isExportOnly = false }) => {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = useState('totalCajas');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="sort-icon-neutral" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp size={14} className="sort-icon-active" /> : 
      <ArrowDown size={14} className="sort-icon-active" />;
  };

  // Calculate weight and volume for each product, then sort
  const enrichedProducts = useMemo(() => {
    const enriched = products.map(product => {
      const productoData = productos.find(p => p.id === product.id);
      const pesoTotalKg = productoData?.peso_total_caja_kg || 0;
      const volumenM3 = productoData?.volumen_m3 || 0;
      
      return {
        ...product,
        totalWeight: product.totalCajas * pesoTotalKg,
        totalVolume: product.totalCajas * volumenM3
      };
    });

    // Sort products
    return enriched.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'nombre':
          aValue = a.nombre?.toLowerCase() || '';
          bValue = b.nombre?.toLowerCase() || '';
          break;
        case 'code':
          aValue = a.code?.toLowerCase() || '';
          bValue = b.code?.toLowerCase() || '';
          break;
        case 'totalCajas':
          aValue = a.totalCajas || 0;
          bValue = b.totalCajas || 0;
          break;
        case 'totalWeight':
          aValue = a.totalWeight || 0;
          bValue = b.totalWeight || 0;
          break;
        case 'totalVolume':
          aValue = a.totalVolume || 0;
          bValue = b.totalVolume || 0;
          break;
        case 'totalMontoUSD':
          aValue = a.totalMontoUSD || 0;
          bValue = b.totalMontoUSD || 0;
          break;
        case 'totalMontoCLP':
          aValue = a.totalMontoCLP || 0;
          bValue = b.totalMontoCLP || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [products, productos, sortColumn, sortDirection]);

  // Calculate totals
  const totals = enrichedProducts.reduce((acc, product) => ({
    totalCajas: acc.totalCajas + product.totalCajas,
    totalMontoUSD: acc.totalMontoUSD + (product.totalMontoUSD || 0),
    totalMontoCLP: acc.totalMontoCLP + (product.totalMontoCLP || 0),
    totalWeight: acc.totalWeight + product.totalWeight,
    totalVolume: acc.totalVolume + product.totalVolume
  }), { totalCajas: 0, totalMontoUSD: 0, totalMontoCLP: 0, totalWeight: 0, totalVolume: 0 });

  // Get limit status for export orders
  const weightStatus = showLimits && isExportOnly ? getLimitColorStatus(totals.totalWeight, EXPORT_WEEKLY_MAX_WEIGHT_KG) : null;
  const volumeStatus = showLimits && isExportOnly ? getLimitColorStatus(totals.totalVolume, EXPORT_WEEKLY_MAX_VOLUME_M3) : null;

  return (
    <div className="products-summary-card">
      <div className="card-body">
        {products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p className="empty-text">{t('orders.noProducts')}</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th className="th-product th-sortable" onClick={() => handleSort('nombre')}>
                    <div className="th-content">
                      <span>{t('inventory.product')}</span>
                      {getSortIcon('nombre')}
                    </div>
                  </th>
                  <th className="th-code th-sortable" onClick={() => handleSort('code')}>
                    <div className="th-content">
                      <span>{t('inventory.code')}</span>
                      {getSortIcon('code')}
                    </div>
                  </th>
                  <th className="th-boxes th-sortable" onClick={() => handleSort('totalCajas')}>
                    <div className="th-content">
                      <span>{t('orders.totalBoxes')}</span>
                      {getSortIcon('totalCajas')}
                    </div>
                  </th>
                  <th className="th-weight th-sortable" onClick={() => handleSort('totalWeight')}>
                    <div className="th-content">
                      <span>Peso (kg)</span>
                      {getSortIcon('totalWeight')}
                    </div>
                  </th>
                  <th className="th-volume th-sortable" onClick={() => handleSort('totalVolume')}>
                    <div className="th-content">
                      <span>Volumen (m³)</span>
                      {getSortIcon('totalVolume')}
                    </div>
                  </th>
                  <th className="th-amount th-sortable" onClick={() => handleSort('totalMontoUSD')}>
                    <div className="th-content">
                      <span>MONTO USD</span>
                      {getSortIcon('totalMontoUSD')}
                    </div>
                  </th>
                  <th className="th-amount th-sortable" onClick={() => handleSort('totalMontoCLP')}>
                    <div className="th-content">
                      <span>MONTO CLP</span>
                      {getSortIcon('totalMontoCLP')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrichedProducts.map((product) => (
                  <tr key={product.id}>
                    <td className="td-product">
                      <div className="product-cell">
                        <div className="product-icon">
                          <Package size={18} />
                        </div>
                        <span className="product-name">{product.nombre}</span>
                      </div>
                    </td>
                    <td className="td-code">
                      <span className="product-code">{product.code}</span>
                    </td>
                    <td className="td-boxes">
                      <div className="boxes-cell">
                        <Box size={16} />
                        <span className="boxes-value">{product.totalCajas}</span>
                      </div>
                    </td>
                    <td className="td-weight">
                      <div className="weight-cell">
                        <Weight size={16} />
                        <span className="weight-value">
                          {Math.round(product.totalWeight).toLocaleString('es-CL')} <span className="unit-label">kg</span>
                        </span>
                      </div>
                    </td>
                    <td className="td-volume">
                      <div className="volume-cell">
                        <Boxes size={16} />
                        <span className="volume-value">
                          {product.totalVolume.toLocaleString('es-CL', { 
                            minimumFractionDigits: 3, 
                            maximumFractionDigits: 3 
                          })} <span className="unit-label">m³</span>
                        </span>
                      </div>
                    </td>
                    <td className="td-amount">
                      <div className="amount-cell">
                        <span className="amount-value">
                          {(product.totalMontoUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </td>
                    <td className="td-amount">
                      <div className="amount-cell">
                        <span className="amount-value">
                          {Math.round(product.totalMontoCLP || 0).toLocaleString('es-CL')}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="2" className="td-totals-label">Totales:</td>
                  <td className="td-boxes">
                    <div className="boxes-cell">
                      <Box size={16} />
                      <span className="boxes-value">{totals.totalCajas}</span>
                    </div>
                  </td>
                  <td className="td-weight">
                    <div className={`weight-cell ${weightStatus ? `limit-${weightStatus}` : ''}`}>
                      <Weight size={16} />
                      <span className="weight-value">
                        {showLimits && isExportOnly ? (
                          <>
                            {formatLimitDisplay(Math.round(totals.totalWeight), EXPORT_WEEKLY_MAX_WEIGHT_KG, 'kg')}
                          </>
                        ) : (
                          <>
                            {Math.round(totals.totalWeight).toLocaleString('es-CL')} <span className="unit-label">kg</span>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="td-volume">
                    <div className={`volume-cell ${volumeStatus ? `limit-${volumeStatus}` : ''}`}>
                      <Boxes size={16} />
                      <span className="volume-value">
                        {showLimits && isExportOnly ? (
                          <>
                            {formatLimitDisplay(parseFloat(totals.totalVolume.toFixed(3)), EXPORT_WEEKLY_MAX_VOLUME_M3, 'm³')}
                          </>
                        ) : (
                          <>
                            {totals.totalVolume.toLocaleString('es-CL', { 
                              minimumFractionDigits: 3, 
                              maximumFractionDigits: 3 
                            })} <span className="unit-label">m³</span>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="td-amount">
                    <div className="amount-cell">
                      <span className="amount-value">
                        {totals.totalMontoUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </td>
                  <td className="td-amount">
                    <div className="amount-cell">
                      <span className="amount-value">
                        {Math.round(totals.totalMontoCLP).toLocaleString('es-CL')}
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <style jsx>{`
        .products-summary-card {
          background: white;
          border-radius: var(--radius-lg);
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }

        .card-body {
          padding: var(--spacing-6);
        }

        .empty-state {
          text-align: center;
          padding: var(--spacing-8) var(--spacing-4);
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: var(--spacing-3);
        }

        .empty-text {
          color: var(--gray-600);
          font-size: var(--font-size-base);
          margin: 0;
        }

        .products-table-container {
          overflow-x: auto;
        }

        .products-table {
          width: 100%;
          border-collapse: collapse;
        }

        .products-table thead {
          background-color: var(--gray-50);
        }

        .products-table th {
          padding: var(--spacing-3) var(--spacing-4);
          text-align: left;
          font-size: var(--font-size-sm);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid var(--gray-200);
        }

        .th-sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
        }

        .th-sortable:hover {
          background-color: var(--gray-100);
        }

        .th-content {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          justify-content: space-between;
        }

        .th-boxes .th-content,
        .th-weight .th-content,
        .th-volume .th-content,
        .th-amount .th-content {
          justify-content: flex-end;
        }

        .sort-icon-neutral {
          color: var(--gray-400);
          opacity: 0.5;
        }

        .sort-icon-active {
          color: var(--primary-blue);
        }

        .th-sortable:hover .sort-icon-neutral {
          opacity: 1;
        }

        .products-table td {
          padding: var(--spacing-4);
          border-bottom: 1px solid var(--gray-200);
        }

        .products-table tbody tr {
          transition: background-color 0.2s;
        }

        .products-table tbody tr:hover {
          background-color: var(--gray-50);
        }

        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }

        .product-icon {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-md);
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary-blue);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-name {
          font-weight: 600;
          color: var(--gray-900);
        }

        .product-code {
          font-family: monospace;
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          background: var(--gray-100);
          padding: var(--spacing-1) var(--spacing-2);
          border-radius: var(--radius-sm);
        }

        .boxes-cell,
        .weight-cell,
        .volume-cell,
        .amount-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
        }

        .boxes-cell {
          color: var(--primary-blue);
        }

        .boxes-value {
          font-weight: 700;
          font-size: var(--font-size-lg);
        }

        .weight-cell {
          color: #0ea5e9;
        }

        .weight-value {
          font-weight: 700;
          font-size: var(--font-size-lg);
        }

        .volume-cell {
          color: #f59e0b;
        }

        .volume-value {
          font-weight: 700;
          font-size: var(--font-size-lg);
        }

        .unit-label {
          font-size: var(--font-size-sm);
          font-weight: 500;
          color: var(--gray-600);
          margin-left: var(--spacing-1);
        }

        .amount-cell {
          color: var(--secondary-green);
        }

        .amount-value {
          font-weight: 700;
          font-size: var(--font-size-lg);
        }

        .th-boxes,
        .th-weight,
        .th-volume,
        .th-amount {
          text-align: right;
        }

        .td-boxes,
        .td-weight,
        .td-volume,
        .td-amount {
          text-align: right;
        }

        .totals-row {
          background-color: var(--gray-100);
          font-weight: 700;
          border-top: 2px solid var(--gray-300);
        }

        .td-totals-label {
          text-align: right;
          font-weight: 700;
          color: var(--gray-900);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .limit-success {
          color: #10b981 !important;
        }

        .limit-warning {
          color: #f59e0b !important;
          font-weight: 700;
        }

        .limit-danger {
          color: #ef4444 !important;
          font-weight: 700;
        }

        .limit-success .weight-value,
        .limit-success .volume-value {
          color: #10b981;
        }

        .limit-warning .weight-value,
        .limit-warning .volume-value {
          color: #f59e0b;
        }

        .limit-danger .weight-value,
        .limit-danger .volume-value {
          color: #ef4444;
        }

        @media (max-width: 768px) {
          .card-header {
            padding: var(--spacing-4);
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-stats {
            width: 100%;
          }

          .stat-badge {
            flex: 1;
            justify-content: center;
          }

          .card-body {
            padding: var(--spacing-4);
          }

          .products-table {
            font-size: var(--font-size-sm);
          }

          .products-table th,
          .products-table td {
            padding: var(--spacing-2) var(--spacing-3);
          }

          .product-icon {
            width: 28px;
            height: 28px;
          }

          .th-code,
          .td-code,
          .th-weight,
          .td-weight,
          .th-volume,
          .td-volume {
            display: none;
          }

          .td-totals-label {
            font-size: var(--font-size-sm);
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsSummaryCard;
